/**
 * RestoID-specific copy (bilingual RU/EN).
 * ---------------------------------------------------------------------------
 * Only strings the upstream Logto dictionary has NO slot for live here
 * (taglines, the "Get code" CTA, method descriptions, etc). Anything that maps
 * to a real Logto phrase key is rendered with i18next `t(...)` in the pages, so
 * Logto Custom Phrases keep overriding it without code changes.
 *
 * Wording comes from the RestoID design source (`custom/design-reference/app.js`
 * COPY), but the table is NOT a mirror of it: it carries only the keys this build
 * actually renders. Keys that frozen source has and no page here requests have
 * been dropped deliberately — do not re-sync them back in.
 */
import { useTranslation } from 'react-i18next';

export type Lang = 'ru' | 'en';
type Entry = Record<Lang, string>;

export const COPY = {
  tagline: {
    ru: 'Единый вход для всех сервисов экосистемы RestoCapital',
    en: 'One login for the entire RestoCapital ecosystem',
  },
  backShort: { ru: 'Назад', en: 'Back' },
  getCode: { ru: 'Получить код', en: 'Get code' },
  phoneLabel: { ru: 'Номер телефона', en: 'Phone number' },
  allOptions: { ru: 'Все варианты входа', en: 'All sign-in options' },
  resendIn: { ru: 'Отправить повторно через', en: 'Resend in' },
  resend: { ru: 'Отправить код повторно', en: 'Resend code' },
  sec: { ru: 'с', en: 's' },
  sentTo: { ru: 'Код отправлен на', en: 'Code sent to' },
  edit: { ru: 'Изменить', en: 'Edit' },
  methodCode: { ru: 'Код из СМС', en: 'SMS code' },
  methodCodeD: { ru: 'Одноразовый код на телефон', en: 'One-time code to your phone' },
  methodPass: { ru: 'Пароль', en: 'Password' },
  methodPassD: { ru: 'Войти по паролю аккаунта', en: 'Sign in with your password' },
  methodPasskey: { ru: 'Ключ доступа', en: 'Passkey' },
  methodPasskeyD: {
    ru: 'Face ID, Touch ID или ключ безопасности',
    en: 'Face ID, Touch ID or a security key',
  },
  // Single-line label for the sign-in screen passkey button (simpler / less prominent
  // than the two-line method row used on the verification-methods page).
  passkeyInline: { ru: 'Войти с Face ID или ключом', en: 'Sign in with Face ID or a key' },
  // Passkey enrollment screen (post sign-in). Cleaner than Logto's default
  // "ключ-пароль / YubiKey" copy; benefit-first and consistent with our terms.
  passkeySetupTitle: { ru: 'Входите быстрее', en: 'Sign in faster' },
  passkeySetupDesc: {
    ru: 'Настройте вход по Face ID, Touch ID или ключу доступа — в следующий раз код из СМС не понадобится.',
    en: 'Set up sign-in with Face ID, Touch ID or a passkey — no SMS code needed next time.',
  },
  passkeySetupCta: { ru: 'Создать ключ доступа', en: 'Create a passkey' },
  // MFA onboarding ("turn on 2FA") — cleaner than Logto's verbose default.
  mfaOnboardTitle: { ru: 'Защитите свой аккаунт', en: 'Protect your account' },
  mfaOnboardDesc: {
    ru: 'Двухфакторная аутентификация добавляет второй шаг проверки при входе — ключ доступа, приложение-аутентификатор или резервные коды.',
    en: 'Two-factor authentication adds a second verification step at sign-in — a passkey, authenticator app or backup codes.',
  },
  mfaOnboardCta: { ru: 'Включить', en: 'Turn on' },
  // MFA passkey binding.
  mfaPasskeyTitle: { ru: 'Создайте ключ доступа', en: 'Create a passkey' },
  mfaPasskeyDesc: {
    ru: 'Второй шаг входа — Face ID, Touch ID или ключ безопасности.',
    en: 'Sign in without a password using Face ID, Touch ID or a security key.',
  },
  mfaPasskeyCta: { ru: 'Создать ключ доступа', en: 'Create a passkey' },
  // MFA authenticator-app (TOTP) binding.
  totpQrStep: {
    ru: 'Отсканируйте QR-код в приложении-аутентификаторе',
    en: 'Scan the QR code in your authenticator app',
  },
  totpKeyStep: { ru: 'Или введите ключ вручную', en: 'Or enter the key manually' },
  totpCopied: { ru: 'Скопировано', en: 'Copied' },
  totpEnterCode: { ru: 'Введите код из приложения', en: 'Enter the code from your app' },
  // MFA factor picker rows (replaces Logto's MfaFactorList — e.g. "Пароль"/USB for
  // passkey). For TOTP we suggest Яндекс Ключ first for the CIS market.
  mfaKeyT: { ru: 'Ключ доступа', en: 'Passkey' },
  mfaKeyD: {
    ru: 'Face ID, Touch ID или ключ безопасности',
    en: 'Face ID, Touch ID or a security key',
  },
  mfaTotpT: { ru: 'Приложение-аутентификатор', en: 'Authenticator app' },
  mfaTotpD: {
    ru: 'Яндекс Ключ, Google Authenticator и др.',
    en: 'Google Authenticator, Yandex ID, etc.',
  },
  mfaBackupT: { ru: 'Резервный код', en: 'Backup code' },
  mfaBackupD: { ru: 'Одноразовые коды восстановления', en: 'One-time recovery codes' },
  mfaEmailT: { ru: 'Код на почту', en: 'Email code' },
  mfaEmailD: { ru: 'Одноразовый код на почту', en: 'One-time code to your email' },
  mfaPhoneT: { ru: 'Код из СМС', en: 'SMS code' },
  mfaPhoneD: { ru: 'Одноразовый код на телефон', en: 'One-time code to your phone' },
  // MFA verification screens — concise copy (cleaner than Logto's verbose defaults).
  mfaVerifyTotpDesc: {
    ru: 'Введите код из приложения-аутентификатора.',
    en: 'Enter the code from your authenticator app.',
  },
  mfaVerifyPasskeyTitle: { ru: 'Подтвердите ключом доступа', en: 'Verify with your passkey' },
  mfaVerifyPasskeyDesc: {
    ru: 'Подтвердите вход с помощью Face ID, Touch ID или ключа безопасности.',
    en: 'Confirm sign-in with Face ID, Touch ID or a security key.',
  },
  mfaVerifyPasskeyCta: { ru: 'Подтвердить ключом доступа', en: 'Verify with a passkey' },
  mfaVerifyBackupDesc: {
    ru: 'Используйте один из сохранённых резервных кодов.',
    en: 'Use one of your saved backup codes.',
  },
  mfaSaveCodesDesc: {
    ru: 'Каждый код можно использовать один раз. Храните их в надёжном месте.',
    en: 'Each code works once. Keep them somewhere safe.',
  },
  mfaBackupLabel: { ru: 'Резервный код', en: 'Backup code' },
  termsPre: { ru: 'Нажимая кнопку ', en: 'By tapping ' },
  termsBtn: { ru: '«Получить код»', en: '“Get code”' },
  termsMid: { ru: ', вы принимаете ', en: ', you accept the ' },
  termsPriv: { ru: 'политику конфиденциальности', en: 'privacy policy' },
  termsAnd: { ru: ' и даёте согласие на ', en: ' and consent to the ' },
  termsData: {
    ru: 'обработку персональных данных',
    en: 'processing of your personal data',
  },
  /* --- screens ported from upstream in the second pass ------------------- */
  // Continue / complete-profile.
  profileTitle: { ru: 'Завершите профиль', en: 'Complete your profile' },
  // /continue/emailOrPhone — switches the one identifier field between the two
  // modes. Logto's own action.switch_to ("Изменить на {{method}}") interpolates a
  // raw method name and reads as a settings toggle, not an inline choice.
  useEmailInstead: { ru: 'Ввести адрес почты', en: 'Use an email address' },
  usePhoneInstead: { ru: 'Ввести номер телефона', en: 'Use a phone number' },
  // Consent.
  consentAuthorize: { ru: 'Разрешить', en: 'Authorize' },
  consentDeny: { ru: 'Отклонить', en: 'Deny' },
  // Switch account.
  useAnother: { ru: 'Войти в другой аккаунт', en: 'Use another account' },
  // Device activation.
  deviceCodeLabel: { ru: 'Код устройства', en: 'Device code' },
  deviceConfirmSub: {
    ru: 'Убедитесь, что код совпадает с показанным на устройстве, и продолжите вход здесь.',
    en: 'Check that the code matches the one on your device, then continue signing in here.',
  },
  or: { ru: 'или', en: 'or' },
  yandexLogin: { ru: 'Войти с Яндекс ID', en: 'Log in with Yandex ID' },
} satisfies Record<string, Entry>;

export type CopyKey = keyof typeof COPY;

/** The active i18next language, normalized to RU-first `ru` | `en`. */
export const useLang = (): Lang => {
  const { i18n } = useTranslation();
  // `i18n.language` is TYPED as string but is genuinely undefined until i18next
  // finishes initializing: reading it unguarded threw and took the whole tree down
  // to a blank page (ResetPasswordLanding's test covers exactly that). The type
  // says this guard is redundant; the runtime disagrees, so it stays.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return (i18n.language ?? '').toLowerCase().startsWith('ru') ? 'ru' : 'en';
};

/** Pick the RestoID copy table for the active i18next language (RU-first). */
export const useCopy = () => {
  const lang = useLang();
  return (key: CopyKey): string => COPY[key][lang];
};
